/**
 * IMPORTANT:
 *
 * This file is used to define the types for the FSS bridge.
 * It is used to ensure that the types are consistent between the FSS and the client.
 *
 * IF YOU NEED TO MAKE A CHANGE TO THE TYPES, YOU MUST HAVE A CORRESPONDING PR TO MAKE
 * CHANGES TO THE FSS SIDE AT THE SAME TIME.
 *
 * `metaserver/static/js/dropbox_ai_assistant/dash_chat/bridge_types.ts`
 */

/**
 * Conversation source constants - string enum for type safety and runtime access
 */
export enum ConversationSource {
  FssUnscoped = "fss-unscoped",
  FssFile = "fss-file",
  FssFilePreview = "fss-file-preview",
  FssFolder = "fss-folder",
  FssSerpQuery = "fss-serp-query",
  FssSerpTypeahead = "fss-serp-typeahead",
  FssStack = "fss-stack",
}

/**
 * Array of all conversation source values for runtime iteration
 */
export const CONVERSATION_SOURCES = Object.values(ConversationSource);

/**
 * The fields required to show a basic representation of an FSS entity in Dash UI.
 */
export type FSSEntityWithFileId = FSSFile | FSSFolderWithFileId;

export type FSSFile = {
  type: "file";
  fileId: string;
  name: string;
  href: string | undefined;
};

export type FSSFolder = {
  type: "folder";
  fileId?: string; // fileId is required for folder summarization but not easily accessible by SERP
  path: string;
};

export type FSSFolderWithFileId = FSSFolder & {
  fileId: string; // fileId is required for folder summarization
};

type ConversationEventBase = {
  eventType: "conversation";
  source: ConversationSource;
  surface: string;
  data: unknown;
  changeScope?: boolean;
  agentIds?: string[];
  scenario?: {
    type: string;
    id: string;
  };
};

type OpenUnscopedSidebarEvent = ConversationEventBase & {
  source: ConversationSource.FssUnscoped;
  data: {
    createNewSession?: boolean;
    searchFss: boolean;
  };
};

type SendFileMessage = ConversationEventBase & {
  source: ConversationSource.FssFile;
  data: {
    createNewSession?: boolean;
    agentIds?: string[];
    files: FSSFile[];
  };
};

type SendFilePreviewMessage = ConversationEventBase & {
  source: ConversationSource.FssFilePreview;
  data: {
    createNewSession?: boolean;
    agentIds?: string[];
    file: FSSFile;
  };
};

type SendFolderMessage = ConversationEventBase & {
  source: ConversationSource.FssFolder;
  data: {
    createNewSession?: boolean;
    agentIds?: string[];
    folders: FSSFolder[];
  };
};

type SendSearchQueryMessage = ConversationEventBase & {
  source: ConversationSource.FssSerpQuery;
  data: {
    searchSessionId: string;
    searchQuery: string;
    createNewSession?: boolean;
    searchFss?: boolean;
  };
};

type SendSearchTypeaheadMessage = ConversationEventBase & {
  source: ConversationSource.FssSerpTypeahead;
  data: {
    searchSessionId: string;
    searchQuery: string;
    createNewSession?: boolean;
    searchFss?: boolean;
    docIds?: string[];
  };
};

type SendStackMessage = ConversationEventBase & {
  source: ConversationSource.FssStack;
  data: {
    createNewSession?: boolean;
    files: FSSFile[]; // TODO: update to stackIds once we support stack-scoped conversations
  };
};

export type UnavailableSource = {
  source: FSSEntityWithFileId;
  localizedMessage: string;
};

export type ConversationEvent =
  | OpenUnscopedSidebarEvent
  | SendFileMessage
  | SendFilePreviewMessage
  | SendFolderMessage
  | SendSearchQueryMessage
  | SendSearchTypeaheadMessage
  | SendStackMessage;

/**
 * type for Actions that can be sent or received from the FSS side
 */
type ActionEventBase = {
  eventType: "action";
  source: string;
  data?: unknown;
  scenario?: {
    type: string;
    id: string;
  };
};

type AckEvent = ActionEventBase & {
  source: "ack";
  data: {
    uuid: string;
  };
};

type ReadyPageEvent = ActionEventBase & {
  source: "ready.page";
  data: {
    pathname: string;
  };
};

type UpdateThemeEvent = ActionEventBase & {
  source: "update.theme";
  data: {
    darkSetting: string; // should match DarkSetting type in Dash Chat
  };
};

type OpenSourcePickerEvent = ActionEventBase & {
  source: "open.source-picker";
  data?: undefined;
};

type AddSourceEvent = ActionEventBase & {
  source: "add.source";
  data: {
    sources: FSSEntityWithFileId[];
  };
};

type CloseSidebarEvent = ActionEventBase & {
  source: "close.sidebar";
  data?: undefined;
};

type OpenSessionHistoryEvent = ActionEventBase & {
  source: "open.session-history";
  data?: undefined;
};

type NotifyLoginEvent = ActionEventBase & {
  source: "notify.login";
  data: {
    userId: number;
  };
};

type NotifyLogoutEvent = ActionEventBase & {
  source: "notify.logout";
  data?: undefined;
};

type UpdateConversationEvent = ActionEventBase & {
  source: "update.conversation";
  data: {
    conversationId: string;
  };
};

type OpenUnavailableSourcesEvent = ActionEventBase & {
  source: "open.unavailable-sources";
  data: {
    unavailableSources: UnavailableSource[];
  };
};

type UpdateCloseButtonEvent = ActionEventBase & {
  source: "update.close-button";
  data?: {
    hide: boolean;
  };
};

type TranscribeFilesEvent = ActionEventBase & {
  source: "transcribe.files";
  data: {
    files: FSSFile[];
  };
};

type OpenPAPEventLogEvent = ActionEventBase & {
  source: "open.pap-event-log";
};

type ClosePAPEventLogEvent = ActionEventBase & {
  source: "close.pap-event-log";
};

// STACKS EVENTS START
type StacksEventBase = {
  eventType: "stacks";
  source: string;
  data?: unknown;
};

type StacksPageEvent = StacksEventBase & {
  source: "stacks_page";
};

type StackDetailPageEvent = StacksEventBase & {
  source: "stack_detail_page";
  data: {
    stackId: string;
    stackName?: string;
    isChatEnabled?: boolean;
    files?: FSSFile[];
  };
};

type NewStackPageEvent = StacksEventBase & {
  source: "new_stack_page";
};

// Sent from stack detail page iframe to parent and then converted to SendStackMessage to be sent to DashChat iframe
export type StackChatEvent = StacksEventBase & {
  source: "stack_chat";
  data: {
    createNewSession?: boolean;
    files: FSSFile[]; // TODO: update to stackIds once we support stack-scoped conversations
  };
};

/**
 * Type for Stacks events that can be sent from Dash to FSS
 */
type DashStacksEvent =
  | StacksPageEvent
  | StackDetailPageEvent
  | NewStackPageEvent
  | StackChatEvent;
// STACKS EVENTS END

/**
 * Action events that can be sent from Dash to FSS
 */
type DashActionEvent =
  | ReadyPageEvent
  | OpenSourcePickerEvent
  | OpenSessionHistoryEvent
  | CloseSidebarEvent
  | NotifyLoginEvent
  | NotifyLogoutEvent
  | UpdateConversationEvent
  | OpenUnavailableSourcesEvent
  | UpdateCloseButtonEvent
  | OpenPAPEventLogEvent
  | ClosePAPEventLogEvent
  | TranscribeFilesEvent;

type TranscribeFileStatusNonError = {
  file: FSSFile;
  status: "checking" | "transcribing" | "completed";
};

export type TranscribeFileStatusError = {
  file: FSSFile;
  status: "error";
  localizedError: string;
};

export type TranscribeFileStatus =
  | TranscribeFileStatusNonError
  | TranscribeFileStatusError;

type TranscribeFilesResponseEvent = ActionEventBase & {
  source: "transcribe.files.response";
  data: {
    fileIdToTranscriptStatus: Record<string, TranscribeFileStatus>;
  };
};

/**
 * Action events that can be sent from FSS to Dash
 */
export type FSSActionEvent = { surface: string } & (
  | AddSourceEvent
  | UpdateConversationEvent
  | UpdateThemeEvent
  | OpenPAPEventLogEvent
  | ClosePAPEventLogEvent
  | TranscribeFilesResponseEvent
);

/**
 * Messages that can be sent from FSS to Dash
 */
export type FSSMessageEvent = ConversationEvent | FSSActionEvent;

export type FSSMessageEventWithUuid = {
  uuid: string;
} & FSSMessageEvent;

export type ConversationEventWithUuid = {
  uuid: string;
} & ConversationEvent;

/**
 * Messages that can be sent from Dash to FSS
 */
export type DashMessageEvent = DashActionEvent | DashStacksEvent | AckEvent;

export type DashMessageEventWithUuid = {
  uuid: string;
} & DashMessageEvent;
